import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import { generate as generateUniqueKey } from "shortid";

import {
  Modal,
  Row,
  Column,
  Input,
  Form,
  Table,
  TableHead,
  TableBody,
  Button,
  Icon,
} from "../../../../shared/components";

/**
 * Prop interface
 */
export interface SetEnvironmentProps {
  resultId: number;
  variables: { [key: string]: string };

  isOpen: boolean;
  onCancel?: () => void;
  onUpdate?: () => void;
  updateEnvVars: (payload: {}, params: {}) => void;
}

/**
 * State interface
 */
export interface SetEnvironmentState {
  formValid: boolean;
  formValues: {};
  formKeys: string[];
}

/**
 * Page component
 */
class SetEnvironment extends React.Component<
  SetEnvironmentProps,
  SetEnvironmentState
> {
  constructor(props: SetEnvironmentProps) {
    super(props);

    this.state = {
      formValid: false,
      formValues: {},
      formKeys: [],
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: SetEnvironmentProps) {
    if (
      (this.props.isOpen === false && nextProps.isOpen) ||
      !isEqual(this.props.variables, nextProps.variables)
    ) {
      this.setState({ formKeys: Object.keys(nextProps.variables || {}) });
    }
  }

  public shouldComponentUpdate(
    nextProps: SetEnvironmentProps,
    nextState: SetEnvironmentState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public render() {
    const { isOpen, onCancel, variables = {} } = this.props;

    const { formValid, formKeys } = this.state;

    const rootStyle = classnames("code-set-environment");

    const envs: {}[] = [];
    const validation = {};

    formKeys.forEach((key) => {
      const keyName = `form-${key}-key`;
      const valueName = `form-${key}-value`;

      envs.push(
        <Row key={key}>
          <Column>
            <Input name={`${keyName}`} />
          </Column>
          <Column>
            <Input name={`${valueName}`} />
          </Column>
          <Column size={1} className="col-delete">
            <a className="delete" onClick={() => this.onRemoveItem(key)} />
          </Column>
        </Row>,
      );

      validation[`${keyName}`] = ["string", "required"];
      validation[`${valueName}`] = ["string", "required"];
    });

    const initialValues = Object.keys(variables).reduce(
      (result, key) =>
        Object.assign(result, {
          [`form-${key}-key`]: key,
          [`form-${key}-value`]: variables[key],
        }),
      {},
    );

    return (
      <Modal
        className={rootStyle}
        title="環境変数の設定"
        isOpen={isOpen}
        onClose={onCancel}
        onClickCancel={onCancel}
        onClickOk={this.onSubmit}
        disableOk={!formValid}
        okButtonLabel="設定する"
      >
        <Form
          initialValues={initialValues}
          validation={validation}
          onFormChange={this.onFormChange}
          clear={!isOpen}
        >
          <p>ここに説明書き</p>
          <Table bordered={true}>
            <TableHead>
              <Column>キー</Column>
              <Column>値</Column>
              <Column size={2}>
                <Button size="small" onClick={this.onAddItem} disabled={false}>
                  <Icon type="plus" />
                  <span>追加</span>
                </Button>
              </Column>
            </TableHead>
            <TableBody>{envs}</TableBody>
          </Table>
        </Form>
      </Modal>
    );
  }

  private onSubmit = () => {
    const { formKeys, formValues } = this.state;

    const variables = formKeys.reduce((result, key) => {
      const keyName = `form-${key}-key`;
      const valueName = `form-${key}-value`;

      return Object.assign(result, {
        [`${formValues[keyName]}`]: formValues[valueName],
      });
    }, {});

    this.props.updateEnvVars({ variables }, this.props.resultId);

    if (typeof this.props.onUpdate === "function") {
      this.props.onUpdate();
    }
  };

  private onFormChange = (
    formValid: boolean,
    formValues: { [key: string]: string },
  ) => {
    this.setState({ formValid, formValues });
  };

  private onAddItem = () => {
    this.setState({ formKeys: [...this.state.formKeys, generateUniqueKey()] });
  };

  private onRemoveItem = (targetKey: string) => {
    const formKeys = this.state.formKeys.filter((key) => key !== targetKey);
    this.setState({ formKeys });
  };
}

export default SetEnvironment;
